import { useEffect, useState } from 'react';
import { OpenPositionListItem } from 'api/types/httpsTypes/dex-trade';
import { useTokenPrice } from 'api/hooks/useTokenPrice';
import { safeBig } from 'utils/FormatNumber';

export interface NetUnrealizedPnl {
  absolute: string | null;
  relative: string | null;
  grossRelative: string | null | undefined;
}

export const useNetUnrealizedLivePnl = (
  position: OpenPositionListItem,
  chainAssetPrice: string | undefined | null
): NetUnrealizedPnl => {
  const {
    chain,
    tokenAddress,
    priceImpact,
    sellTax,
    totalAmountTokenRemaining,
    averageBuyPriceInUsd,
    totalAmountTokenBought,
    totalBuyAmountInEth,
    currentTokenPriceInUsd,
  } = position;

  const [pnl, setPnl] = useState<NetUnrealizedPnl>({
    absolute: null,
    relative: null,
    grossRelative: null,
  });

  const tokenAssetPrice = useTokenPrice(chain, tokenAddress) ?? null;

  useEffect(() => {
    const boughtAmount = safeBig(totalAmountTokenBought);
    const boughtInChainAsset = safeBig(totalBuyAmountInEth);
    const remainingAmount = safeBig(totalAmountTokenRemaining);
    const priceImpactMultiplier = safeBig(1).minus(safeBig(priceImpact));
    const sellTaxMultiplier = safeBig(1).minus(safeBig(sellTax));
    const chainPrice = safeBig(chainAssetPrice);
    const marketPrice = safeBig(tokenAssetPrice ?? currentTokenPriceInUsd);

    // Required values must be present
    if (marketPrice.eq(0) || chainPrice.eq(0) || boughtAmount.eq(0)) {
      return;
    }

    // 1. Value of remaining tokens in chain asset
    const unrealizedValueInChainAsset = remainingAmount
      .mul(marketPrice)
      .div(chainPrice);

    // 2. Adjust for sell tax and price impact
    const unrealizedNetValueInChainAsset = unrealizedValueInChainAsset
      .mul(sellTaxMultiplier)
      .mul(priceImpactMultiplier);

    // 3. Proportional cost basis of remaining amount
    const proportionRemaining = remainingAmount.div(boughtAmount);
    const unrealizedCostBasis = proportionRemaining.mul(boughtInChainAsset);

    // 4. Net PnL = Net Value - Cost Basis
    const unrealizedNetPnlAmount =
      unrealizedNetValueInChainAsset.minus(unrealizedCostBasis);

    // 5. Relative and gross PnL
    const relativePnl = unrealizedCostBasis.gt(0)
      ? unrealizedNetValueInChainAsset.div(unrealizedCostBasis).minus(1)
      : safeBig(0);

    const grossRelativePnl = unrealizedCostBasis.gt(0)
      ? unrealizedValueInChainAsset.div(unrealizedCostBasis).minus(1)
      : safeBig(0);

    const newPnl: NetUnrealizedPnl = {
      absolute: unrealizedNetPnlAmount.toString(),
      relative: relativePnl.toString(),
      grossRelative: grossRelativePnl.toString(),
    };

    setPnl((prev) =>
      prev.absolute !== newPnl.absolute ||
      prev.relative !== newPnl.relative ||
      prev.grossRelative !== newPnl.grossRelative
        ? newPnl
        : prev
    );
  }, [
    tokenAssetPrice,
    chainAssetPrice,
    chain,
    tokenAddress,
    priceImpact,
    sellTax,
    totalAmountTokenRemaining,
    averageBuyPriceInUsd,
    totalBuyAmountInEth,
    totalAmountTokenBought,
    currentTokenPriceInUsd,
  ]);

  return pnl;
};
